import React, { useState, useEffect } from 'react';
import {
    Card,
    DashedAddButton,
    Divider,
    FieldGroup,
    Grid,
    IconButton,
    Modal,
    ModalFooter,
    PillButton,
    Row,
    Text,
    TextButton,
    TimePicker,
} from 'library/ui';
import { Colors } from 'library';
import { Clear, InfoOutlined } from '@mui/icons-material';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import {
    approveReport,
    clearBreak,
    clearClockOut,
    getTimestampByType,
    neverReturnedFromBreak,
} from '../HourReportMethods';

// eslint-disable-next-line import/named
import moment, { Moment } from 'moment-timezone';
import { IHourReport } from '../../../internal/payments/HourReportModels';
import { consoleLogInDev } from 'shared';
import { buildTimeInZone, toTimeInput } from 'shared/shiftTime';
import ConfirmDialog from 'reusableComponents/confirmDialog';

export function ChangeHours({
    report,
    onCancel,
    onDone,
}: {
    report: IHourReport;
    onCancel: () => void;
    onDone: () => void;
}) {
    const [loading, setLoading] = useState(false);

    const [breakStartErr, setBreakStartErr] = useState('');
    const [breakEndErr, setBreakEndErr] = useState('');
    const [shiftStartErr, setShiftStartErr] = useState('');
    const [shiftEndErr, setShiftEndErr] = useState('');
    const [showLeftAtBreakConfirm, setShowLeftAtBreakConfirm] = useState(false);

    const initialBreakStart =
        getTimestampByType(report, 'business_break_start') ??
        getTimestampByType(report, 'provider_altered_break_start') ??
        getTimestampByType(report, 'provider_break_start');
    const initialBreakEnd =
        getTimestampByType(report, 'business_break_end') ??
        getTimestampByType(report, 'provider_altered_break_end') ??
        getTimestampByType(report, 'provider_break_end');

    const [breakStart, setBreakStart] = useState<Moment | null>(
        initialBreakStart ? moment.tz(initialBreakStart, report.timezone) : null,
    );
    const [breakEnd, setBreakEnd] = useState<Moment | null>(
        initialBreakEnd ? moment.tz(initialBreakEnd, report.timezone) : null,
    );
    const [showBreak, setShowBreak] = useState<boolean>(!!(initialBreakStart || initialBreakEnd));

    const initialShiftStart =
        getTimestampByType(report, 'business_clock_in') ??
        getTimestampByType(report, 'provider_altered_clock_in') ??
        getTimestampByType(report, 'provider_clock_in') ??
        report.start;
    const initialShiftEnd =
        getTimestampByType(report, 'business_clock_out') ??
        getTimestampByType(report, 'provider_altered_clock_out') ??
        getTimestampByType(report, 'provider_clock_out') ??
        report.end;

    const [shiftEnd, setShiftEnd] = useState<Moment | null>(
        initialShiftEnd ? moment.tz(initialShiftEnd, report.timezone) : null,
    );
    const [shiftStart, setShiftStart] = useState<Moment | null>(
        initialShiftStart ? moment.tz(initialShiftStart, report.timezone) : null,
    );

    useEffect(() => {
        validateShiftStart();
        validateShiftEnd();
        validateBreakStart();
        validateBreakEnd();
    }, [shiftStart, shiftEnd, breakStart, breakEnd]);

    const validateBreakStart = () => {
        if (!breakStart) return;
        const start = moment(breakStart);
        setBreakStartErr('');

        if (shiftEnd && start.isAfter(shiftEnd)) {
            setBreakStartErr("can't be after the end time");
            return;
        }
        if (start.isBefore(shiftStart)) {
            setBreakStartErr("can't be before the start time");
            return;
        }
        if (start.isAfter(breakEnd)) {
            setBreakStartErr("can't be after the break end time");
            return;
        }
        if (start && !breakEnd) {
            setBreakEndErr('is required if break start is set');
            return;
        }
        setBreakStartErr('');
    };

    const validateBreakEnd = () => {
        if (!breakEnd) return;

        const end = moment(breakEnd);

        setBreakEndErr('');

        if (shiftStart && end.isBefore(shiftStart)) {
            setBreakEndErr("can't be before the start time");
            return;
        }
        if (end.isAfter(shiftEnd)) {
            setBreakEndErr("can't be after the end time");
            return;
        }
        if (end.isBefore(breakStart)) {
            setBreakEndErr("can't be before the break start time");
            return;
        }
        setBreakEndErr('');
    };

    const validateShiftEnd = () => {
        if (!shiftEnd) return;

        const end = moment(shiftEnd);
        setShiftEndErr('');

        if (end.isSame(shiftStart)) {
            setShiftEndErr("can't be the same as the start time");
            return;
        }
        if (end.isBefore(shiftStart)) {
            setShiftEndErr("can't be before the shift start time");
            return;
        }
        if (breakStart && end.isBefore(breakStart)) {
            setShiftEndErr("can't be before the break start time");
            return;
        }
        if (breakEnd && end.isBefore(breakEnd)) {
            setShiftEndErr("can't be before the break end time");
            return;
        }
        setShiftEndErr('');
    };

    const validateShiftStart = () => {
        if (!shiftStart) return;

        const start = moment(shiftStart);
        setShiftStartErr('');

        if (start.isSame(shiftEnd)) {
            setShiftStartErr("can't be the same as the end time");
            return;
        }
        if (shiftEnd && start.isAfter(shiftEnd)) {
            setShiftStartErr("can't be after the end time");
            return;
        }
        if (breakStart && start.isAfter(breakStart)) {
            setShiftStartErr("can't be after the break start time");
            return;
        }
        if (breakEnd && start.isAfter(breakEnd)) {
            setShiftStartErr("can't be after the break end time");
            return;
        }
        setShiftStartErr('');
    };

    function setTime(val: Moment | null, setTimeFunction: (value: Moment | null) => void) {
        if (shiftStart && val) {
            const dateObj = {
                year: shiftStart?.year(),
                month: shiftStart?.month(),
                date: shiftStart?.date(),
            };

            val.set(dateObj);
        }

        setTimeFunction(val);
    }

    function submitChangeRequest() {
        setLoading(true);
        const body = {
            business_clock_in: shiftStart ? shiftStart.toISOString() : undefined,
            business_clock_out: shiftEnd ? shiftEnd.toISOString() : undefined,
            business_break_start: breakStart ? breakStart.toISOString() : undefined,
            business_break_end: breakEnd ? breakEnd.toISOString() : undefined,
            business_approved_date: shiftStart && shiftEnd ? new Date().toISOString() : undefined,
        };
        approveReport(report.id, body).catch(consoleLogInDev).finally(onDone);
    }

    function onClearBreak() {
        setBreakStart(null);
        setBreakEnd(null);
        setShowBreak(false);
        clearBreak(report.id).catch(consoleLogInDev);
    }

    function onClearClockOut() {
        setShiftEnd(null);
        clearClockOut(report.id).catch(consoleLogInDev);
    }

    function onNeverReturnedFromBreak() {
        setShiftEnd(breakStart);
        setBreakStart(null);
        setBreakEnd(null);
        neverReturnedFromBreak(report.id)
            .catch(consoleLogInDev)
            .finally(() => {
                setShowLeftAtBreakConfirm(false);
                onDone();
            });
    }

    const firstName = report.provider.user.first_name;
    const showLeftAtBreak = breakStart && !breakEnd;
    const confirmDisabled = loading || !!breakStartErr || !!breakEndErr || !!shiftEndErr || !!shiftStartErr;

    return (
        <>
            <Grid columns={2} gap="md" collapseAt="sm">
                <FieldGroup label="Start">
                    <TimePicker
                        fullWidth
                        value={toTimeInput(shiftStart, report.timezone)}
                        onChange={(value) =>
                            setShiftStart(buildTimeInZone(value, shiftStart ?? report.start, report.timezone))
                        }
                    />
                </FieldGroup>
                <FieldGroup label="End">
                    <Row gap="sm" align="center">
                        <TimePicker
                            fullWidth
                            value={toTimeInput(shiftEnd, report.timezone)}
                            onChange={(value) =>
                                setTime(buildTimeInZone(value, shiftEnd ?? report.start, report.timezone), setShiftEnd)
                            }
                        />
                        {shiftEnd && <IconButton icon={Clear} onClick={onClearClockOut} />}
                    </Row>
                </FieldGroup>
            </Grid>
            {showBreak ? (
                <>
                    <Grid columns={2} gap="md" collapseAt="sm">
                        <FieldGroup label="Break Start">
                            <TimePicker
                                fullWidth
                                value={toTimeInput(breakStart, report.timezone)}
                                onChange={(value) =>
                                    setTime(
                                        buildTimeInZone(value, breakStart ?? report.start, report.timezone),
                                        setBreakStart,
                                    )
                                }
                            />
                        </FieldGroup>
                        <FieldGroup label="Break End">
                            <TimePicker
                                fullWidth
                                value={toTimeInput(breakEnd, report.timezone)}
                                onChange={(value) =>
                                    setTime(
                                        buildTimeInZone(value, breakEnd ?? report.start, report.timezone),
                                        setBreakEnd,
                                    )
                                }
                            />
                        </FieldGroup>
                    </Grid>
                    <Row gap="md" align="center" wrap>
                        <TextButton onClick={onClearBreak}>Remove break</TextButton>
                        {showLeftAtBreak && (
                            <TextButton onClick={() => setShowLeftAtBreakConfirm(true)}>
                                {firstName} never returned from break
                            </TextButton>
                        )}
                    </Row>
                </>
            ) : (
                <>
                    <Card tone="muted">
                        <Row gap="sm" align="start">
                            <InfoOutlined sx={{ fontSize: 18, color: Colors.textSecondary, marginTop: '2px' }} />
                            <Text tone="secondary">
                                {firstName} didn&rsquo;t report a break. If they took one, fill in the break times
                                below.
                            </Text>
                        </Row>
                    </Card>
                    <DashedAddButton onClick={() => setShowBreak(true)}>Add a break</DashedAddButton>
                </>
            )}
            <Divider />
            <ModalFooter>
                <PillButton variant="secondary" onClick={onCancel}>
                    Cancel
                </PillButton>
                <PillButton disabled={confirmDisabled} onClick={submitChangeRequest}>
                    {loading ? 'Saving...' : 'Confirm changes'}
                </PillButton>
            </ModalFooter>
            <ConfirmDialog
                open={showLeftAtBreakConfirm}
                message={`Please confirm ${firstName} never returned from break`}
                onConfirm={() => onNeverReturnedFromBreak()}
                onClose={() => setShowLeftAtBreakConfirm(false)}
            />
        </>
    );
}

export default function ChangeModal({
    changeReport,
    setChangeReport,
    updateHoursReported,
}: {
    changeReport: IHourReport;
    setChangeReport: (r: IHourReport | undefined) => void;
    updateHoursReported: () => void;
}) {
    function close() {
        updateHoursReported();
        setChangeReport(undefined);
    }

    return (
        <Modal
            title={`Change ${changeReport?.provider.user.first_name}'s hours · ${formatShortWeekdayMonthDay(
                changeReport.start ?? new Date(),
            )}`}
            onClose={close}
        >
            <ChangeHours report={changeReport} onCancel={close} onDone={close} />
        </Modal>
    );
}
